import { useCubeQuery } from "../../../../utils/hooks/useCubeQuery";
import useCubeClient from "../../../../utils/cubejs-client";
import { Button } from "antd";
import Table from "../../../../components/lists/Table/Table";
import React, { useMemo } from "react";
import { CompanyType } from "../../../../__generated__/graphql";
import { 
  AdvertiserCostTableProps, 
  createCostQuery,
  processCubeData,
  getCostTableColumns,
  exportToExcelRevenue
} from "./costUtils";

const AdvertiserCostTable: React.FC<AdvertiserCostTableProps> = ({ user, queryEnd, queryStart }) => {
  const cubejsApi = useCubeClient();

  // Create cube query with utility function
  const cubeQuery = useMemo(() => createCostQuery(queryStart, queryEnd), [queryStart, queryEnd]);

  // Execute the query - use type assertion to avoid TypeScript errors with the complex cube.js types
  const { resultSet, error, isLoading } = useCubeQuery(cubeQuery as any, { cubejsApi: cubejsApi as any });

  // Process data with memoization to prevent unnecessary recalculations
  const data = useMemo(() => {
    // Handle errors and loading states
    if (error) {
      console.error('Cube query error:', error);
      return [];
    }
    
    if (!resultSet) {
      return [];
    }

    return processCubeData(resultSet.tablePivot ? (resultSet.tablePivot() as any) : []);
  }, [resultSet, error]);

  // Get columns configuration
  const websiteColumns = useMemo(() => getCostTableColumns(), []);

  // Calculate summary data for total row
  const getSummaryData = useMemo(() => {
    const totalReads = data.reduce((sum, row) => sum + (row.reads || 0), 0);
    const totalCost = data.reduce((sum, row) => sum + (row.Cost || 0), 0);

    return {
      campaignName: 'TOTAL',
      reads: totalReads.toString(),
      Cost: totalCost.toString()
    };
  }, [data]);

  // Only show for agency users
  if (user.company.type !== CompanyType.Agency) {
    return null;
  }

  return (
    <div>
      {error && (
        <div className="p-4 text-red-600">
          Error loading data. Please try again.
        </div>
      )}
      <Table
        className="mt-4"
        columns={websiteColumns}
        data={data}
        searchable={false}
        loading={isLoading}
        showSummary={true}
        summaryData={getSummaryData}
        footer={() => (
          <Button onClick={() => exportToExcelRevenue(data, "WebsitesData")}>
            Export to Excel
          </Button>
        )}
      />
    </div>
  );
};

export default React.memo(AdvertiserCostTable);
